import React, { useEffect, useState } from 'react';
import { Modal, Input, Form, Radio, InputNumber, Alert, Button, Space, Divider } from 'antd';
import { MdOutlineTextDecrease, MdOutlineTextIncrease } from "react-icons/md";
import { EditOutlined, RobotOutlined } from "@ant-design/icons";

// Mensaje automático de sistema (debe coincidir con IA_PENALTY_MESSAGE del backend).
export const IA_PENALTY_MESSAGE =
    "El sistema ha detectat, mitjançant l'anàlisi de metadades i de la telemetria de " +
    "l'activitat (patrons de comportament, interaccons amb altres aplicatius/webs i pèrdues de focus), l'ús d'IA " +
    "generativa durant la realització d'aquesta pràctica. Per aquest motiu, la nota " +
    "d'aquesta entrega s'ha fixat automàticament a 0.";

type PenaltyMode = 'subtract' | 'add' | 'override';

export interface PenaltyApplyData {
    tipus: 'ADJUST' | 'OVERRIDE';
    penalty: number;        // para ADJUST: punts (+ resta / - suma)
    notaFixada?: number;    // para OVERRIDE: nota 0-100
    reason: string;
    esIa?: boolean;
}

interface PenaltyModalProps {
    open: boolean;
    onClose: () => void;
    onApply: (data: PenaltyApplyData) => void;
    initialPenalty?: number;
    initialMode?: 'subtract' | 'add';
}

const PenaltyModal: React.FC<PenaltyModalProps> = ({ open, onClose, onApply, initialPenalty = 0, initialMode = 'subtract' }) => {
    const [form] = Form.useForm();
    const [mode, setMode] = useState<PenaltyMode>('subtract');

    useEffect(() => {
        if (open) {
            form.resetFields();
            form.setFieldsValue({ penalty: initialPenalty, notaFixada: 0, reason: '' });
            setMode(initialMode);
        }
        // eslint-disable-next-line
    }, [open, initialPenalty, initialMode]);

    const handleOk = () => {
        form
            .validateFields()
            .then((values) => {
                let data: PenaltyApplyData;
                if (mode === 'override') {
                    // Nota fija que machaca la nota calculada.
                    data = {
                        tipus: 'OVERRIDE',
                        penalty: 0,
                        notaFixada: Number(values.notaFixada),
                        reason: values.reason
                    };
                } else {
                    // ADJUST: restar => valor positivo; sumar => valor negativo.
                    data = {
                        tipus: 'ADJUST',
                        penalty: mode === 'subtract' ? Math.abs(values.penalty) : -Math.abs(values.penalty),
                        reason: values.reason
                    };
                }
                form.resetFields();
                onApply(data);
                onClose();
            })
            .catch((info) => {
                console.log('Validate Failed:', info);
            });
    };

    // Acción rápida: còpia per IA => OVERRIDE nota 0 + mensaje de sistema.
    const applyCopiaIA = () => {
        onApply({
            tipus: 'OVERRIDE',
            penalty: 0,
            notaFixada: 0,
            reason: IA_PENALTY_MESSAGE,
            esIa: true
        });
        onClose();
    };

    const title = mode === 'override'
        ? "Fixar nota (sobreescriu la nota)"
        : (mode === 'subtract' ? "Aplicar Penalització (Resta Punts)" : "Aplicar Bonificació (Suma Punts)");

    const okText = mode === 'override' ? "Fixar nota" : (mode === 'subtract' ? "Restar" : "Sumar");

    return (
        <Modal
            open={open}
            title={title}
            okText={okText}
            cancelText="Cancel·lar"
            onCancel={onClose}
            onOk={handleOk}
            width={520}
        >
            <Form
                form={form}
                layout="vertical"
                name="form_in_modal"
                initialValues={{ penalty: 0, notaFixada: 0, reason: '' }}
            >
                <Form.Item label="Tipus de modificació">
                    <Radio.Group value={mode} onChange={(e) => setMode(e.target.value)}>
                        <Radio.Button value="subtract"><MdOutlineTextDecrease /> Restar Punts</Radio.Button>
                        <Radio.Button value="add"><MdOutlineTextIncrease /> Sumar Punts</Radio.Button>
                        <Radio.Button value="override"><EditOutlined /> Fixar nota</Radio.Button>
                    </Radio.Group>
                </Form.Item>

                {mode === 'override' ? (
                    <>
                        <Alert
                            type="warning"
                            showIcon
                            style={{ marginBottom: 12 }}
                            message="Fixar nota sobreescriu la nota calculada pels tests."
                            description="S'aplica encara que l'alumne torni a enviar el codi. Si hi ha diverses notes fixades, preval la més baixa."
                        />
                        <Form.Item
                            name="notaFixada"
                            label="Nota final (0 - 100)"
                            rules={[{ required: true, message: 'Introdueix la nota a fixar!' }]}
                        >
                            <InputNumber min={0} max={100} step={1} style={{ width: '100%' }} />
                        </Form.Item>
                    </>
                ) : (
                    <Form.Item
                        name="penalty"
                        label={mode === 'subtract' ? "Punts a restar" : "Punts a sumar"}
                        rules={[{ required: true, message: 'Si us plau, introdueix la quantitat de punts!' }]}
                    >
                        <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
                    </Form.Item>
                )}

                <Form.Item
                    name="reason"
                    label="Motiu"
                    rules={[{ required: true, message: 'Si us plau, introdueix el motiu!' }]}
                >
                    <Input.TextArea rows={3} placeholder={
                        mode === 'override'
                            ? "Ex: Nota fixada per decisió del professorat..."
                            : (mode === 'subtract' ? "Ex: Retard en l'entrega..." : "Ex: Codi excel·lent, participació extra...")
                    } />
                </Form.Item>

                <Divider style={{ margin: '8px 0' }} />
                <Space direction="vertical" style={{ width: '100%' }}>
                    <span style={{ fontSize: 12, color: '#8c8c8c' }}>Acció ràpida</span>
                    <Button danger icon={<RobotOutlined />} onClick={applyCopiaIA} block>
                        Suspendre per còpia / ús d'IA (nota 0)
                    </Button>
                </Space>
            </Form>
        </Modal>
    );
};

export default PenaltyModal;
